/**
 * Server selbst zusammenstellen.
 *
 * Der Kaufdialog fragt „welches Modell?“. Dieser hier fragt „welche Maschine?“
 * – und das ist die Frage, die man im Betrieb tatsächlich stellt. Ein
 * Mailserver braucht Einschübe und kaum Kerne, ein VPS-Host das Gegenteil.
 *
 * Der Aufbau folgt der Reihenfolge, in der man einen Server baut:
 *
 *   Gehäuse ──► Sockel ──► Speicher ──► Laufwerke ──► Netzwerkkarte
 *
 * Rechts stehen dabei durchgehend die Folgen: Kerne, Gigabyte, Watt, Preis. Sie
 * ändern sich mit jedem Klick, weil genau darin der Nutzen liegt – wer die
 * Wirkung erst nach dem Kauf sieht, hat nicht konfiguriert, sondern geraten.
 *
 * Gerechnet wird mit `configureServer` aus dem Kern, derselben Funktion, die
 * anschließend auch der Kaufbefehl prüft. Zwei Rechenwege für dieselbe Frage
 * wären zwei Gelegenheiten, verschiedene Antworten zu geben.
 */
import { useEffect, useMemo, useState } from 'react';
import type { PartKind, PartModel, ServerRole } from '@hosting-sim/core';
import { PART_KIND_LABEL, configureServer, suggestConfiguration } from '@hosting-sim/core';
import { Badge, Button, Field, Input, Meter, Modal, Notice, Select } from './ui';
import { useGame } from '../store/game';
import { money, num, watt } from '../lib/format';
import type { api } from '../lib/api';

/** Ein Gehäuse, wie es der Katalog-Endpunkt liefert. */
interface ChassisEntry {
  key: string;
  name: string;
  price: number;
  heightUnits: number;
  vendorTier: string;
  description: string;
  platform: {
    cpuSockets: number;
    cpuSocket: string;
    dimmSlots: number;
    dimmStandard: string;
    maxRamGb: number;
    driveBays: number;
    driveFormFactors: string[];
    nicSlots: number;
    gpuSlots?: number;
  };
}

const KIND_ORDER: PartKind[] = ['cpu', 'ram', 'drive', 'nic', 'gpu'];

const ROLE_LABEL: Record<ServerRole, string> = {
  'shared-hosting': 'Shared Hosting',
  'vps-host': 'VPS-Host',
  dedicated: 'Dedicated',
  storage: 'Storage',
  mail: 'Mail',
  dns: 'DNS',
  backup: 'Backup',
  'gpu-host': 'GPU-Host',
  unassigned: 'Nicht zugewiesen',
};

export function ServerConfiguratorDialog({
  open,
  onClose,
  catalog,
}: {
  open: boolean;
  onClose: () => void;
  catalog: Awaited<ReturnType<typeof api.catalog>> | null;
}) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);

  const chassisList = ((catalog as unknown as { chassis?: ChassisEntry[] })?.chassis ?? []) as ChassisEntry[];
  const parts = useMemo(
    () => (catalog?.parts ?? { cpu: [], ram: [], drive: [], nic: [], gpu: [] }) as Record<PartKind, PartModel[]>,
    [catalog],
  );
  const allParts = useMemo(() => KIND_ORDER.flatMap((kind) => parts[kind] ?? []), [parts]);

  const [chassisKey, setChassisKey] = useState('');
  const [lines, setLines] = useState<{ modelKey: string; quantity: number }[]>([]);
  const [rackId, setRackId] = useState('');
  const [role, setRole] = useState<ServerRole>('vps-host');
  const [name, setName] = useState('');

  const racks = world ? Object.values(world.racks) : [];

  // Gehäusewechsel setzt die Bestückung zurück: Ein DDR4-Modul hat in einem
  // DDR5-Brett nichts verloren, und stillschweigend mitgenommene Auswahl wäre
  // schlimmer als eine leere.
  useEffect(() => {
    if (!open) return;
    const first = chassisKey || chassisList[0]?.key || '';
    if (!chassisKey && first) setChassisKey(first);
    if (first) setLines(suggestConfiguration(first, allParts));
  }, [open, chassisKey, chassisList, allParts]);

  useEffect(() => {
    if (!rackId && racks.length > 0) setRackId(racks[0].id);
  }, [racks, rackId]);

  const chassis = chassisList.find((c) => c.key === chassisKey);
  const configuration = useMemo(() => configureServer({ chassisKey, parts: lines }), [chassisKey, lines]);

  const quantityOf = (modelKey: string) => lines.find((line) => line.modelKey === modelKey)?.quantity ?? 0;

  const setQuantity = (modelKey: string, quantity: number) => {
    setLines((current) => {
      const next = current.filter((line) => line.modelKey !== modelKey);
      if (quantity > 0) next.push({ modelKey, quantity });
      return next;
    });
  };

  if (!open) return null;

  return (
    <Modal open onClose={onClose} wide title="Server konfigurieren">
      <div className="space-y-4">
        {chassisList.length === 0 ? (
          <Notice tone="warning">Der Gehäusekatalog konnte nicht geladen werden.</Notice>
        ) : (
          <>
            {/* --- Gehäuse ------------------------------------------------- */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Select label="Gehäuse" value={chassisKey} onChange={(e) => setChassisKey(e.target.value)}>
                {chassisList.map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {entry.name} — {money(entry.price)}
                  </option>
                ))}
              </Select>
              <Select label="Zielrack" value={rackId} onChange={(e) => setRackId(e.target.value)}>
                {racks.map((rack) => (
                  <option key={rack.id} value={rack.id}>
                    {world?.datacenters[rack.datacenterId]?.city} – {rack.name}
                  </option>
                ))}
              </Select>
              <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value as ServerRole)}>
                {Object.entries(ROLE_LABEL)
                  .filter(([value]) => value !== 'unassigned')
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
              <Input label="Name (optional)" value={name} placeholder="wird automatisch vergeben" onChange={(e) => setName(e.target.value)} />
            </div>

            {chassis && (
              <p className="text-xs text-panel-500 dark:text-panel-400">
                {chassis.description} · {chassis.heightUnits} HE · Sockel {chassis.platform.cpuSocket} ·{' '}
                {chassis.platform.dimmStandard.toUpperCase()} bis {chassis.platform.maxRamGb} GB ·{' '}
                {chassis.platform.driveFormFactors.join(', ')}
              </p>
            )}

            <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
              {/* --- Bauteile --------------------------------------------- */}
              <div className="space-y-4">
                {KIND_ORDER.map((kind) => {
                  const slot = configuration.slots.find((s) => s.kind === kind);
                  const options = (parts[kind] ?? []).filter((part) => compatible(chassis, part));
                  return (
                    <div key={kind}>
                      <div className="mb-1.5 flex items-baseline justify-between">
                        <p className="label">{PART_KIND_LABEL[kind]}</p>
                        <span className={`text-2xs tabular ${slot && slot.used > slot.total ? 'text-red-600 dark:text-red-400' : 'text-panel-500'}`}>
                          {slot?.used ?? 0} / {slot?.total ?? 0} {slot?.label}
                        </span>
                      </div>

                      {options.length === 0 ? (
                        <p className="text-2xs text-panel-500">Für dieses Gehäuse ist nichts verfügbar.</p>
                      ) : (
                        <div className="space-y-1">
                          {options.map((part) => {
                            const quantity = quantityOf(part.key);
                            return (
                              <div
                                key={part.key}
                                className={`surface-muted flex flex-wrap items-center gap-2 p-2 text-xs ${quantity > 0 ? 'ring-1 ring-accent-500/40' : ''}`}
                              >
                                <span className="min-w-0 flex-1 truncate font-medium">{part.name}</span>
                                <span className="tabular shrink-0 text-panel-500">{money(part.price)}</span>
                                <div className="flex shrink-0 items-center gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => setQuantity(part.key, Math.max(0, quantity - 1))} disabled={quantity === 0}>
                                    −
                                  </Button>
                                  <span className="tabular w-6 text-center">{quantity}</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setQuantity(part.key, quantity + 1)}
                                    disabled={!!slot && slot.used >= slot.total}
                                  >
                                    +
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* --- Ergebnis --------------------------------------------- */}
              <div className="space-y-3 lg:sticky lg:top-0 lg:self-start">
                <div className="surface-muted p-3">
                  <p className="label mb-2">Diese Maschine</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Kerne">{num(configuration.specs.cpuCores)}</Field>
                    <Field label="Takt">{configuration.specs.cpuGhz > 0 ? `${configuration.specs.cpuGhz} GHz` : '–'}</Field>
                    <Field label="Arbeitsspeicher">{num(configuration.specs.ramGb)} GB</Field>
                    <Field label="Speicher">{num(configuration.specs.ssdGb + configuration.specs.hddGb)} GB</Field>
                    <Field label="Anbindung">
                      {configuration.specs.nicMbps >= 1000
                        ? `${configuration.specs.nicMbps / 1000} Gbit/s`
                        : `${configuration.specs.nicMbps} Mbit/s`}
                    </Field>
                    <Field label="Leistungsaufnahme">{watt(configuration.specs.maxWatt)}</Field>
                  </div>

                  {configuration.slots.map((slot) => (
                    <div key={slot.kind} className="mt-2">
                      <Meter
                        value={slot.used}
                        max={Math.max(1, slot.total)}
                        label={
                          <>
                            <span>{PART_KIND_LABEL[slot.kind]}</span>
                            <span className="tabular">
                              {slot.used}/{slot.total}
                            </span>
                          </>
                        }
                      />
                    </div>
                  ))}
                </div>

                <div className="surface-muted p-3">
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-panel-500">Gehäuse</span>
                    <span className="tabular">{money(configuration.chassisPrice)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-panel-500">Bauteile</span>
                    <span className="tabular">{money(configuration.partsPrice)}</span>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between border-t divider pt-1 text-sm font-medium">
                    <span>Gesamt</span>
                    <span className="tabular">{money(configuration.totalPrice)}</span>
                  </div>
                  <p className="mt-1 text-2xs text-panel-500">zzgl. {money(configuration.maintenancePerMonth)}/Monat Wartung</p>
                </div>

                {configuration.problems.map((problem) => (
                  <Notice key={problem} tone="danger">
                    {problem}
                  </Notice>
                ))}
                {configuration.warnings.map((warning) => (
                  <Notice key={warning} tone="warning">
                    {warning}
                  </Notice>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Badge tone="muted">{chassis?.vendorTier}</Badge>
                <Button size="sm" variant="ghost" onClick={() => setLines(suggestConfiguration(chassisKey, allParts))}>
                  Vorschlag zurücksetzen
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  disabled={!configuration.ok || racks.length === 0}
                  onClick={async () => {
                    const result = await run({
                      type: 'hw/buildServer',
                      rackId: rackId as never,
                      chassisKey,
                      parts: lines,
                      role,
                      name: name || undefined,
                    });
                    if (result.ok) onClose();
                  }}
                >
                  Für {money(configuration.totalPrice)} bauen
                </Button>
              </div>
            </div>

            {racks.length === 0 && <Notice tone="warning">Es ist noch kein Rack vorhanden. Zuerst unter „Rechenzentren“ ein Rack anmieten.</Notice>}
          </>
        )}
      </div>
    </Modal>
  );
}

/** Passt ein Bauteil in dieses Gehäuse? Dieselben Regeln wie im Kern. */
function compatible(chassis: ChassisEntry | undefined, part: PartModel): boolean {
  if (!chassis) return false;
  switch (part.kind) {
    case 'ram':
      return part.standard === chassis.platform.dimmStandard;
    case 'cpu':
      return part.socket === chassis.platform.cpuSocket;
    case 'drive':
      return chassis.platform.driveFormFactors.includes(part.formFactor);
    case 'nic':
      return true;
    case 'gpu':
      return (chassis.platform.gpuSlots ?? 0) > 0;
  }
}
